'use client';

import React, { useState } from 'react';
import { useServerInsertedHTML } from 'next/navigation';
import createCache from '@emotion/cache';
import { CacheProvider, ThemeProvider, Global } from '@emotion/react';
import { Theme } from '@radix-ui/themes';
import { warmCozyLight, warmCozyDark } from '@planit.family/theme';
import { globalStyles } from './globalStyles';
import { AuthProvider } from '../context/AuthContext';
import { ThemeModeProvider, useThemeMode } from '../context/ThemeContext';

function InnerRegistry({ children }: { children: React.ReactNode }) {
  const { themeMode } = useThemeMode();
  const activeTheme = themeMode === 'dark' ? warmCozyDark : warmCozyLight;

  return (
    <Theme
      accentColor="amber"
      grayColor="slate"
      radius="small"
      appearance={themeMode}
    >
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      <ThemeProvider theme={activeTheme as any}>
        <Global styles={globalStyles} />
        <AuthProvider>{children}</AuthProvider>
      </ThemeProvider>
    </Theme>
  );
}

export function StyledComponentsRegistry({
  children,
}: {
  children: React.ReactNode;
}) {
  const [emotionCache] = useState(() => {
    const cache = createCache({ key: 'css', prepend: true });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (cache as any).compat = true;
    return cache;
  });

  useServerInsertedHTML(() => {
    const entries = Object.entries(emotionCache.inserted);
    if (entries.length === 0) return null;
    const names = entries.map(([k]) => k).join(' ');
    const styles = entries.map(([, v]) => v).join('\n');
    return (
      <style
        data-emotion={`${emotionCache.key} ${names}`}
        dangerouslySetInnerHTML={{ __html: styles }}
      />
    );
  });

  return (
    <CacheProvider value={emotionCache}>
      <ThemeModeProvider>
        <InnerRegistry>{children}</InnerRegistry>
      </ThemeModeProvider>
    </CacheProvider>
  );
}
